import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { Rss, Bookmark, Share2, FileText, Settings } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/app/routes'
import {
  useActiveTopic,
  getLastViewedTopicId,
} from '@/features/topics/state/useActiveTopic'

type NavKey = 'feed' | 'saved' | 'network' | 'reports' | 'settings'

type BottomNavItemDef = {
  key: NavKey
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>
}

const NAV_ITEMS: BottomNavItemDef[] = [
  { key: 'feed', icon: Rss },
  { key: 'saved', icon: Bookmark },
  { key: 'network', icon: Share2 },
  { key: 'reports', icon: FileText },
  { key: 'settings', icon: Settings },
]

function buildPath(key: NavKey, topicId: string | null): string {
  const id = topicId ?? getLastViewedTopicId()
  if (key === 'settings') return ROUTES.app.settings
  if (!id) return ROUTES.app.topicsList
  if (key === 'feed') return ROUTES.app.topicFeed(id)
  if (key === 'saved') return ROUTES.app.topicSaved(id)
  if (key === 'network') return ROUTES.app.topicNetwork(id)
  return ROUTES.app.topicReports(id)
}

function matchesRoute(key: NavKey, pathname: string): boolean {
  if (key === 'settings') return pathname.startsWith(ROUTES.app.settings)
  if (key === 'feed') return /\/app\/topics\/[^/]+\/feed/.test(pathname)
  if (key === 'saved') return /\/app\/topics\/[^/]+\/saved/.test(pathname)
  if (key === 'network') return /\/app\/topics\/[^/]+\/network/.test(pathname)
  if (key === 'reports') return /\/app\/topics\/[^/]+\/reports/.test(pathname)
  return false
}

export const BottomNav = memo(function BottomNav() {
  const { t } = useTranslation('sidebar')
  const location = useLocation()
  const navigate = useNavigate()
  const { topicId } = useActiveTopic()

  return (
    <nav className="print-hide fixed inset-x-0 bottom-0 z-20 border-t border-border bg-background pb-[env(safe-area-inset-bottom)] lg:hidden">
      <div className="mx-auto flex h-14 max-w-lg items-stretch">
        {NAV_ITEMS.map((item) => {
          const active = matchesRoute(item.key, location.pathname)
          const path = buildPath(item.key, topicId)
          return (
            <BottomNavItem
              key={item.key}
              icon={item.icon}
              label={t(`item.${item.key}`)}
              isActive={active}
              onPress={() => navigate(path)}
            />
          )
        })}
      </div>
    </nav>
  )
})

function BottomNavItem({
  icon: Icon,
  label,
  isActive,
  onPress,
}: {
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>
  label: string
  isActive: boolean
  onPress: () => void
}) {
  return (
    <button
      type="button"
      onClick={onPress}
      className={cn(
        'relative flex flex-1 flex-col items-center justify-center gap-0.5 transition-all duration-150 active:scale-[0.92] active:bg-nav-active-surface',
        isActive ? 'text-foreground' : 'text-muted-foreground',
      )}
    >
      <Icon
        size={20}
        strokeWidth={isActive ? 2.2 : 1.8}
        className={cn(
          'transition-[filter,color] duration-200',
          isActive && 'text-nav-active drop-shadow-[0_0_6px_var(--nav-active-glow)]',
        )}
      />
      <span className="text-[9px] font-medium leading-none tracking-wide">{label}</span>
      {isActive && (
        <span className="absolute top-0 h-[2px] w-6 rounded-full bg-nav-active" />
      )}
    </button>
  )
}
